/*
 * Created on 05.03.19 12:14
 *
 * Copyright(c) 1995 - 2019 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */

import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LanguageService } from '../i18n/language.service';
import { PortalEventService } from '../portal/portal-event.service';
import { LoggingService } from '../utils/logging.service';
import { HttpError } from './http-error';


/**
 * Dieser Interceptor ist da um von jedem fehlgeschlagenen Http-Requests Bescheid zu bekommen.
 * Das sind folgende Fehler:
 * <ul>
 * <li> Responses vom Typ 5XX
 * <li> Responses vom Typ 4XX
 * <li> Kritische Fehler mit Response vom Typ 0 (Browser hat Response des Servers abgelehnt, z.B. CORS-Probleme)
 * <li> Responses vom Typ 2XX, wenn Angular den Body nicht richtig parsen kann
 * </ul>
 * Alle Fehler werden hier geloggt, aber <b>0 und NUR 5XX Fehler</b> an den {@link PortalEventService}
 * propagiert. Es werden alle Fehler weitergeworfen, so dass die aufrufende Stelle auch darauf
 * reagieren kann. <b>4XX Fehler sollen von der aufrufenden Stelle behandelt/angezeigt werden
 * und 5XX Fehler vom Portal</b>. Trotzdem werden alle weitergeleitet, damit in begründenen Fällen
 * auch der aufrufende Block auf alles entsprechend reagieren kann.
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {

    constructor(private portalEventService: PortalEventService,
                private log: LoggingService,
                private languageService: LanguageService) {
    }

    /**
     * Siehe {@link HttpErrorInterceptor}.
     * Nur kritische Fehler (5XX und 0) werden in der Konsole als error dargestelt und im
     * Portal als Fehler oben rechts angezeigt (via PortalEventService). 2XX-Fehler werden
     * hier auf debug geloggt und 4xx Fehler auf trace. Der Vorteil davon sie hier zu loggen
     * statt nur durchfallen zu lassen ist, dass man hier direkt die Response dazu hat statt
     * nur den eigentlichen Fehler. Die hat man sonst nicht immer, ohne dass man manuell
     * ein callback für den error-Fall im subscribe() übergibt.
     * @param req
     * @param next
     */
    intercept<T>(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
        return next.handle(req).pipe(
            // Wird aktiv bei allen Responses, die:
            // - eine 4xx response sind
            // - eine 5xx response sind
            // - eine 0 response sind (Browser hat Response abgelehnt)
            // - ein 200 OK sind, das parsen des Bodys durch Angular aber fehlgeschlagen ist
            catchError((err: HttpErrorResponse) => {
                // wollen nicht alles als "error" loggen, da es die Konsole vollmüllt
                // insbesondere nicht 400, da die zu behandelnden Stellen das schon machen
                const log = this.getLogFunction(err.status);

                log(`HttpErrorInterceptor: Fehler gefunden (status = ${err.status}):`);
                this.logDebugInfoForProbableErrorReason(err.status);

                log(`HttpErrorInterceptor: HttpErrorResponse:`);
                log(err);

                if (HttpErrorInterceptor.propagateToPortalEventService(err.status)) {
                    // den Fehler im Portal in der Error Flash Component anzeigen
                    this.portalEventService.next(
                        HttpError.fromI18N(err.status, this.languageService, this.log)
                    );
                } else {
                    log(`HttpErrorInterceptor: Fehler wurde hier nur geloggt; Behandlung wird der aufrufenden Stelle überlassen`);
                }

                // alle Fehler weiter werfen im Observable, damit die
                // error-callbacks in subscribe() darauf zugreifen können.
                return throwError(() => err)
            }),
        );
    }

    /**
     * Loggt ein paar zusätzliche Infos für den wahrscheinlichen Fehlergrund.
     * @param status Status Code (0 - 999).
     * @private
     */
    private logDebugInfoForProbableErrorReason(status: number) {
        /* if (status === 0) {
            - Kommt bei Connection Refused
            - Kommt wenn Browser die Antwort ablehnt (nicht Angular, sondern wirklich der Browser)
              --> möglicherweise durch Headerfehler, z.B. (nicht nur!) nach dem ersten erfolgreichen Request
              plötzlich ein anderer CORS-Header => CORS-Verletzung`
            );
        }*/
        if (HttpErrorInterceptor.isXxxCode(status, StatusRange.R_2XX)) {
            this.log.debug(
                `HttpErrorInterceptor#catchError: status code 2xx: Fehler von Angular. Wahrscheinlich Fehler beim Parsen des ` +
                `Bodys. Möglicherweise stimmt z.B. der content-type der Response nicht (z.b. text/plain statt ` +
                `application/javascript) oder es wird JSON erwartet, während nur ein String in der Response steht`
            );
        }
    }

    /**
     * Entscheidet welche Logfunktion für einen Fehler genutzt werden soll.
     * Der VORTEIL hier zu Loggen ist es, dass jeder Fehler zusammen mit der
     * Response (Header etc) geloggt wird. Normalerweise fällt nur der Error durch
     * und man sieht die Response nicht automatisch.
     *
     * Gibt eine Referenz auf z.B. {@link LoggingService#debug}, {@link LoggingService#trace}
     * oder {@link LoggingService#error} zurück.
     * @param status Statuscode. 0 bis 999
     * @return function for logging
     * @private
     */
    private getLogFunction(status: number): (msg: any) => void {
        let cb: (msg: any) => void;
        switch (true) {
            // 0 ist kritisch; wahrscheinlich durch eine Service unavailability ausgelöst,
            // die zu einer Headerkombination führte, die der Client ablehnt (aber nicht sicher!)
            case status === 0:
            case HttpErrorInterceptor.isXxxCode(status, StatusRange.R_5XX):
                cb = this.log.error;
                break;

            case HttpErrorInterceptor.isXxxCode(status, StatusRange.R_2XX):
                cb = this.log.debug;
                break;

            // 4XX soll von den aufrufenden Stellen geloggt und gehandhabt werden.
            case HttpErrorInterceptor.isXxxCode(status, StatusRange.R_4XX):
            default:
                cb = this.log.trace;
                break;
        }

        // otherwise "this" is undefined inside the callback function
        return cb.bind(this.log);
    }

    /**
     * Gibt an, ob ein Fehler an den PortalEventService weitergegeben werden soll.
     * Das heißt i.d.R., dass der Fehler im Portal oben rechts angezeigt wird.
     * Das wollen wir nur bei kritischen Fehlern (500 und 0).
     * @param status
     * @private
     */
    private static propagateToPortalEventService(status: number): boolean {
        return status === 0 || HttpErrorInterceptor.isXxxCode(status, StatusRange.R_5XX);
    }

    /**
     * Überprüft, ob ein Code in einer Range liegt.
     * Beispielsweise ob 201 in 2XX liegt.
     * @param status der Status Code
     * @param range die erwartete Range
     * @private
     */
    private static isXxxCode(status, range: StatusRange): boolean {
        return range <= status && status < (range + 100);
    }

}

/**
 * Parameer for {@link HttpErrorInterceptor#isXxxCode}
 */
enum StatusRange {
    R_1XX = 100,
    R_2XX = 200,
    R_3XX = 300,
    R_4XX = 400,
    R_5XX = 500,
    R_6XX = 600,
    R_7XX = 700,
    R_8XX = 800,
    R_9XX = 900,
}
